import { useState, useEffect, useRef, useContext } from 'react'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { BreadCrumb, CustomTable, SnackSimple } from '../../../../../../components'
import { Types } from '../../../../../../utils/enums'
import { input } from './initialState'
import { getList } from '../methods/methods'
import { useContextState } from '../../../../../../hooks/theme'
import { useStyles } from './style'
import { icons } from '../../../../../../utils/icons'
import { httpFetch } from '../../../../../../utils/crud'
import { OptionGroupService } from '../../../../../../service/optionGroup'
import { OptionTableRow } from '../../../../../../service/optionGroup/reponse'
import SimpleModal from '../../../../../../components/modal/simpleModal'
import { handleAllErrors } from '../../../../../../utils/methods/handleErrors'
import { FiltersDrawer } from '../../../../../../components/filtersDrawer'
import { DinamicForm } from '../../../../../../components/dinamicForm'
import { useDownload } from '../../../../../../hooks/download/useDownload'
import { returnQuery, returnQueryWithPagination } from '../../../../../../utils/methods/returnQuery'
import { ModelDownloadButtons } from '../../../../../../components/donwloadFiles/model'
import { OptionGroupContext } from '../../context'
import { useNavigate } from 'react-router-dom'

const OptionGroup = () => {
  const { t } = useTranslation()
  const { colors } = useContextState()
  const navigate = useNavigate()
  const styles = useStyles(colors)
  const inputs = input;
  const [isLoading, setIsLoading] = useState(false)
  const [table, setTable] = useState<OptionTableRow[]>([])
  const refTime = useRef<NodeJS.Timeout | null>(null)
  const { filterMain } = useContext(OptionGroupContext)
  const { counter, errors, filterData, paginationModel, handlePagination, onChange, cleanFilter, sortTable, query, filterString } = filterMain
  const [totalPages, setTotalPages] = useState(0)
  const [openDeleteModal, setOpenDeleteModal] = useState({
    open: false,
    id: null,
  })
  const [openSnackbar, setOpenSnackbar] = useState({
    open: false,
    openModal: false,
    message: '',
    type: Types.SUCCESS,
  })

  const handleCloseSnackbar = (value: boolean) => {
    setOpenSnackbar((prev) => ({ ...prev, open: value }))
  }

  const downloadMain = useDownload({
    docName: t('optionGroup.filter.title'),
    query: returnQueryWithPagination({ filter: filterString, sort: query?.sort, }).replace('&', ''),
    downloadFuntion: OptionGroupService(httpFetch).downloadFile,
  })

  const column: any[] = [
    {
      id: 'name',
      label: t('general.tags.name'),
      sortable: true,
    },
    {
      id: 'optionsCount',
      label: t('general.tags.optionNumber'),
      sortable: true,
    },
    {
      id: 'createTime',
      label: t('general.tags.startDate'),
      sortable: true,
    },
    {
      id: 'action',
      label: t('general.tags.action'),
    },
  ]

  const handleDelete = async () => {
    if (openDeleteModal.id) {
      const response: any = await OptionGroupService(httpFetch).deleteOptionGroupById(openDeleteModal.id)

      const existError = response?.errors
      setOpenSnackbar((prev) => ({
        ...prev,
        open: true,
        message: existError ? handleAllErrors(existError?.[0]?.code ?? '', t, 'optionGroup.modal.delete.error') : t('optionGroup.modal.delete.success'),
        type: existError ? Types.ERROR : Types.SUCCESS,
      }))
      closeDeleteModal()
      if (!existError) {
        getAll()
      }
    }
  }

  const getAll = async () => {
    setIsLoading(true)
    const responseStores = await getList(colors, t, returnQuery({ ...filterMain.query, filter: filterMain.query.filter?.replaceAll('+', '%2B') }))
    const row = responseStores.row.map((item: any) => ({
      ...item,
      action: {
        children: (
          <Box sx={styles.constainerAction}>
            <Tooltip title={t('ToolTips.edit')}>
              <IconButton
                sx={styles.edit}
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`./form/${item.id}`)
                }}
              >
                {icons('edit')}
              </IconButton>
            </Tooltip>
            <Tooltip title={t('ToolTips.delete')}>
              <IconButton
                sx={styles.delete}
                onClick={(e) => {
                  e.stopPropagation()
                  setOpenDeleteModal({
                    open: true,
                    id: item.id,
                  })
                }}
              >
                {icons('delete')}
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    }))

    setTotalPages(responseStores.totalPages)
    setTable(row)
    setIsLoading(false)
  }

  const closeDeleteModal = () => {
    setOpenDeleteModal({
      id: null,
      open: false,
    })
  }

  useEffect(() => {
    if (refTime.current) { clearTimeout(refTime.current) }

    refTime.current = setTimeout(() => {
      getAll()
    }, 300)
  }, [filterMain.query.all])

  return (
    <Box sx={styles.container}>
      <SnackSimple close={handleCloseSnackbar} open={openSnackbar.open} type={openSnackbar.type} message={openSnackbar.message} time={3000} />
      <SnackSimple
        close={downloadMain.closeOpenSnakbar}
        open={downloadMain.openSnackbar.open}
        type={downloadMain.openSnackbar.type as Types}
        message={downloadMain.openSnackbar.message}
      />
      <BreadCrumb
        removeBreadcrumb={['setting']}
        replaceLabels={{ 'option-group': 'settingOptionGroup' }}
      />
      <SimpleModal
        open={openDeleteModal.open}
        description={t('optionGroup.modal.delete.description')}
        close={closeDeleteModal}
        title={t('optionGroup.modal.delete.title')}
        buttonText={t('general.delete')}
        cancelText={t('general.cancel')}
        color={colors}
        back={closeDeleteModal}
        loadingButton
        next={() => {
          handleDelete()
        }}
      />
      <Box sx={{ position: 'relative', paddingRight: '50px' }}>
        <FiltersDrawer cleanFilter={cleanFilter} count={counter} right={0} top={0}>
          <Box sx={styles.section}>
            <DinamicForm errors={errors} input={Object.values(inputs)} values={filterData} onChange={onChange} filter={true} />
          </Box>
        </FiltersDrawer>

        <Box sx={styles.header}>
          <Typography variant='title_grey_big'>{t('optionGroup.filter.title')}</Typography>

          <Box sx={styles.flex}>
            <ModelDownloadButtons
              csv={downloadMain.downloadCSV}
              pdf={downloadMain.downloadPDF}
              word={downloadMain.downloadWORD}
              disableCSV={downloadMain.download.excel}
              disablePDF={downloadMain.download.pdf}
              disableWORD={downloadMain.download.word}
              styleType
            />

            <Tooltip title={t('ToolTips.clearFilters')}>
              <IconButton
                sx={(styles.clean, (counter || 0) > 0 ? null : styles.hide)}
                onClick={() => {
                  cleanFilter()
                }}
              >
                {icons('clean')}
              </IconButton>
            </Tooltip>

            <Tooltip title={t('ToolTips.create')}>
              <IconButton
                sx={styles.clean}
                className='add'
                onClick={() => {
                  navigate('../form')
                }}
              >
                {icons('add')}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <CustomTable
          loader={isLoading}
          color={colors}
          column={column}
          row={table}
          selected={-1}
          order={
            query.sort && {
              count: query?.sort?.count,
              key: query?.sort?.field,
              order: query?.sort?.sort,
            }
          }
          sortable={(field, sort, count) => {
            sortTable(field && sort ? { field: field, sort: sort, count } : undefined)
          }}
          serverSidePagination={{
            page: (paginationModel.page ?? 0) + 1,
            size: paginationModel?.pageSize ?? 20,
            totalPages: totalPages,
          }}
          handleChange={(page: number, pageSize: number) => {
            handlePagination({ page, pageSize })
          }}
        />
      </Box>
    </Box>
  )
}

export default OptionGroup
